/**
 * compactDdlCard — pure model for the compact-popover DDL summary card
 * (§2.9 S13). Classifies the cursor statement's DDL kind + resolves the
 * human-readable facts a later UI task (T3) renders, reusing the VDE's own
 * classification/extraction surfaces end to end: `classifyDdlAtCursor` (VDE)
 * for the coarse `DdlKind`/`DdlObjectKind`, `extractDdlStructure` (SR) for
 * structural facts on its whitelisted heads (try/catch — it throws by design
 * for anything else), and the VDE `parseParked*Statement` scanners
 * (`./compactDdlCard.nameOnly`) for name-only kinds.
 *
 * A local DROP head-reader still owns generic-DROP naming even though
 * `StatementInfo.schemaEffects` is now populated
 * (`sql-grammar/document/schemaEffects.ts`): that shared extractor's
 * `SchemaObjectKind` map has no `domain`/`user`/`login` arms (degrade to
 * `'unknown'`) and folds `PACKAGE` into `'routine'` — too lossy for an honest
 * "DROP DOMAIN" / "DROP USER" / "DROP LOGIN" kind chip.
 */
import { buildParsedDocumentFromSource, getDocumentStructure } from '../../../sql-grammar/document';
import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../../sql-resolver/scope/identifiers';
import { skipWsCommentsLocal, tryConsumeKeyword } from '../../../sql-resolver/ddl/extract/shared';
import { extractDdlStructure } from '../../../sql-resolver/ddl/extractDdlStructure';
import type { ParsedBuffer } from '../../../sql-resolver/types';
import { classifyDdlAtCursor } from '../../visual-ddl-editor/inline/classifyDdlAtCursor';
import type { DdlKind } from '../../visual-ddl-editor/inline/classifyDdlAtCursor';
import type { DdlObjectKind } from '../../visual-ddl-editor/edit/editContext';

import { vqeCopy } from '../ux/vqeCopy';
import { factsFromStructure, objectNameFromStructure, tableRefFromStructure } from './compactDdlCard.facts';
import { nameOnlyObjectName } from './compactDdlCard.nameOnly';

export type DdlCardDesignMode = 'create' | 'alter';

export type DdlCardModel = {
  /** `classifyDdlAtCursor` recognised the statement's DDL kind. */
  readonly recognized: boolean;
  /** 'CREATE TABLE' | 'DROP TABLE' | 'TRUNCATE' | … — always present. */
  readonly kindLabel: string;
  /** From the VDE classification; `null` only when unrecognized. */
  readonly objectKind: DdlObjectKind | null;
  /** Display text (raw qualified name); `null` = omit the row entirely. */
  readonly objectName: string | null;
  /** Display-ready fact lines via `vqeCopy.ddlCard.facts`; `[]` is honest. */
  readonly facts: readonly string[];
  /** `null` → no Design-table button at all (plan-9 decision 3). */
  readonly designTable: {
    readonly mode: DdlCardDesignMode;
    readonly tableName: string;
    readonly schema?: string;
    readonly statementText: string;
  } | null;
};

/** `DdlKind` arms `extractDdlStructure` can extract structure for — mirrors
 *  its own internal head-classification whitelist
 *  (`extractDdlStructure.ts:222-233`). `'drop'` is handled separately below
 *  because its facts (when any) come from `extractDdlStructure` while its
 *  name/label come from the local head-reader. */
const EXTRACT_WHITELIST: ReadonlySet<DdlKind> = new Set([
  'create-table', 'alter-table', 'create-index',
  'create-type', 'create-domain',
  'create-trigger', 'alter-trigger',
  'create-procedure', 'alter-procedure', 'create-function', 'alter-function',
  'grant', 'revoke',
]);

function mechanicalLabel(ddlKind: DdlKind): string {
  return ddlKind.toUpperCase().replace(/-/g, ' ');
}

const DROP_MATERIALIZED_VIEW = 'MATERIALIZED VIEW';
const DROP_OBJECT_KEYWORDS: readonly string[] = [
  'TABLE', 'VIEW', 'SEQUENCE', 'SCHEMA', 'TRIGGER', 'INDEX',
  'PROCEDURE', 'PROC', 'FUNCTION', 'TYPE', 'DOMAIN', 'PACKAGE',
  'USER', 'ROLE', 'LOGIN', 'DATABASE',
];

/** Generic-DROP head reader (plan-9 decision 2): consumes DROP → one object
 *  keyword → optional IF EXISTS → the leading qualified identifier. Unmatched
 *  object keyword → `{ kindLabel: 'DROP', objectName: null }` (honest — the
 *  card never guesses an object kind it can't name). */
function readDropHead(
  source: string,
  options: ParseOptions,
  statement: StatementInfo,
): { readonly kindLabel: string; readonly objectName: string | null } {
  const end = statement.range.endOffset;
  let i = skipWsCommentsLocal(source, statement.range.startOffset, end, options);
  i = tryConsumeKeyword(source, i, end, options, 'DROP');
  i = skipWsCommentsLocal(source, i, end, options);

  let objectKeyword: string | null = null;
  const afterMaterialized = tryConsumeKeyword(source, i, end, options, 'MATERIALIZED');
  if (afterMaterialized !== i) {
    const j = skipWsCommentsLocal(source, afterMaterialized, end, options);
    const afterView = tryConsumeKeyword(source, j, end, options, 'VIEW');
    if (afterView !== j) { objectKeyword = DROP_MATERIALIZED_VIEW; i = afterView; }
  }
  if (objectKeyword === null) {
    for (const kw of DROP_OBJECT_KEYWORDS) {
      const next = tryConsumeKeyword(source, i, end, options, kw);
      if (next !== i) { objectKeyword = kw; i = next; break; }
    }
  }
  if (objectKeyword === null) return { kindLabel: 'DROP', objectName: null };

  i = skipWsCommentsLocal(source, i, end, options);
  const afterIf = tryConsumeKeyword(source, i, end, options, 'IF');
  if (afterIf !== i) {
    const j = skipWsCommentsLocal(source, afterIf, end, options);
    const afterExists = tryConsumeKeyword(source, j, end, options, 'EXISTS');
    if (afterExists !== j) i = skipWsCommentsLocal(source, afterExists, end, options);
  }

  const read = readQualifiedName(source, i, end, options);
  return { kindLabel: `DROP ${objectKeyword}`, objectName: read ? read.qualified.raw : null };
}

function buildDesignTable(
  ddlKind: DdlKind,
  table: { readonly name: string; readonly schema?: string } | null,
  statementText: string,
): DdlCardModel['designTable'] {
  if (!table) return null;
  if (ddlKind === 'create-table') return { mode: 'create', tableName: table.name, schema: table.schema, statementText };
  if (ddlKind === 'alter-table') return { mode: 'alter', tableName: table.name, schema: table.schema, statementText };
  return null;
}

const UNRECOGNIZED: Omit<DdlCardModel, 'kindLabel'> = {
  recognized: false,
  objectKind: null,
  objectName: null,
  facts: [],
  designTable: null,
};

/**
 * Build the DDL compact-card model for one statement's source text. Returns
 * `null` when the text carries no statement at all (blank/whitespace-only —
 * mirrors `classifyDdlForCursor`'s guard). Never throws — every reused
 * extractor call is wrapped so an unsupported/malformed shape degrades to an
 * honest "no facts" model instead of escaping.
 */
export function buildDdlCardModel(statementText: string, options: ParseOptions): DdlCardModel | null {
  if (statementText.trim().length === 0) return null;
  const parsedDocument = buildParsedDocumentFromSource(statementText, options);
  const documentStructure = getDocumentStructure(parsedDocument, 'compact-ddl-card');
  const statement = documentStructure.statements[0];
  if (!statement) return null;

  const classification = classifyDdlAtCursor(statementText, options, statement);
  if (!classification) {
    return { ...UNRECOGNIZED, kindLabel: vqeCopy.workspace.statementKindLabel(statement.kind) };
  }

  const { ddlKind, objectKind } = classification;
  const buffer: ParsedBuffer = { parsedDocument, documentStructure };

  if (ddlKind === 'drop') {
    const head = readDropHead(statementText, options, statement);
    let facts: readonly string[] = [];
    try {
      facts = factsFromStructure(extractDdlStructure(buffer, statement));
    } catch {
      facts = [];
    }
    return {
      recognized: true, kindLabel: head.kindLabel, objectKind,
      objectName: head.objectName, facts, designTable: null,
    };
  }

  if (EXTRACT_WHITELIST.has(ddlKind)) {
    try {
      const structure = extractDdlStructure(buffer, statement);
      return {
        recognized: true,
        kindLabel: mechanicalLabel(ddlKind),
        objectKind,
        objectName: objectNameFromStructure(structure),
        facts: factsFromStructure(structure),
        designTable: buildDesignTable(ddlKind, tableRefFromStructure(structure), statementText),
      };
    } catch {
      return {
        recognized: true, kindLabel: mechanicalLabel(ddlKind), objectKind,
        objectName: null, facts: [], designTable: null,
      };
    }
  }

  return {
    recognized: true,
    kindLabel: mechanicalLabel(ddlKind),
    objectKind,
    objectName: nameOnlyObjectName(ddlKind, statementText),
    facts: [],
    designTable: null,
  };
}
